/* eslint-disable no-underscore-dangle */
import { $t } from '@/i18n';
import { useStore } from '@/store';
import { hasCompleteArtifactCos, loadArtifactCosStylesheets, preloadArtifactCosAssets, disposeArtifactCosStylesheets } from '@/utils/aiProjectInfo';
import { useGogoRemoteCssReset } from './css-reset-counter';
import { useSetupAICheck } from './check/setup';
import { AsyncGogoRemoteHost, loadGogoLib, loadGogoRemoteStuff, loadVue3Stuff } from './runtime-loader';
import { defineComponent, onMounted, onUnmounted, provide, ref, shallowRef } from '@vue/composition-api';
import { getWujiBaseModules } from './useGogoRuntimeEnv';
import { notifyVue3RouterChanged } from './vue3router';
import useAiDepLoader from './xy-logic/use-ai-dep-loader';
import { WUJI_DEPS_KEY } from '@utils/dependencyLoader/consts';
const GOGO_CTX_MODULES_KEY = '__GOGO_CTX_MODULES__';
export default defineComponent({
    name: 'GogoRuntimeRemoteWrapper',
    components: {
        AsyncGogoRemoteHost,
    },
    props: {
        projectId: {
            type: String,
            required: true,
        },
        env: {
            type: String,
            required: true,
        },
        branch: {
            type: String,
            required: true,
        },
    },
    setup(props) {
        useGogoRemoteCssReset();
        const { markReady } = useSetupAICheck();
        const loadLib = loadGogoRemoteStuff();
        const { loadAIPageDependency } = useAiDepLoader();
        const gogoRuntimeViewInstances = shallowRef([]);
        const routeViewContainer = shallowRef(null);
        const gogoHostReady = ref(true);
        const remoteHost = shallowRef(null);
        const src = ref('');
        const search = new URLSearchParams(window.location.search);
        const wsUrl = search.get('wsUrl') || '';
        const hasPreviewBridge = !!search.get('wujiAiPageUK');
        const isPreviewIFrame = hasPreviewBridge && search.get('iframe') === '1';
        const loadStatus = ref(isPreviewIFrame ? 'ready' : 'loading');
        const loadError = ref('');
        provide('gogoRuntimeViewInstances', gogoRuntimeViewInstances);
        provide('routeViewContainer', routeViewContainer);
        provide('gogoHostReady', gogoHostReady);
        provide('gogoPortalHostClass', 'wujiGogo-remote-host');
        function handleHostReady(host) {
            remoteHost.value = host;
            gogoHostReady.value = true;
            markReady(loadLib);
            syncGogoHijackFlag(host);
            watchPageDescriptors(host);
        }
        // 与 GogoRuntimeRouterWrapper 对齐：预览态(存在 __gogoDebugHijack)时，把当前是否命中 gogo 页
        // 同步给 hijack 组件，让根路径 '/' 的 hijack 占位能渲染出 AI 页面。
        // 必须用 vue3 的 watchEffect 才能追踪 remote host 的 vue3 响应式 currentMatchedRoute。
        function syncGogoHijackFlag(host) {
            const gogoHijack = window.__gogoDebugHijack;
            if (!gogoHijack)
                return;
            void loadVue3Stuff().then((stuff) => {
                const { watchEffect } = stuff.vue;
                watchEffect(() => {
                    gogoHijack.currentRouteIsGogoPage = host.context?.currentMatchedRoute?.value?.descriptor?.file || '';
                });
            });
        }
        // pageDescriptors 是 vue3 ref；HMR/新增页时会更新，需用 vue3 watch 才能追踪。
        function watchPageDescriptors(host) {
            void loadVue3Stuff().then((stuff) => {
                const { watch } = stuff.vue;
                watch(() => host.context?.pageDescriptors?.value, (pages) => {
                    console.log('pages', pages);
                    if (Array.isArray(pages)) {
                        notifyVue3RouterChanged({ pages });
                    }
                }, { deep: true, immediate: true });
            });
        }
        function handlePointerDown(e) {
            remoteHost.value?.handlePointerDown?.(e);
        }
        function abortEventIfPicking(e) {
            remoteHost.value?.abortEventIfPicking?.(e);
        }
        const beforeLoadRemoteModule = async () => {
            // 编辑器 bundler 产物会 external `@wuji-gogo/runtime`（patchVueSFCExports 等）
            const gogoRuntime = await loadGogoLib('runtime');
            window[GOGO_CTX_MODULES_KEY] = {
                ...window[GOGO_CTX_MODULES_KEY] || {},
                ...getWujiBaseModules({ projectId: props.projectId }),
                ...(window[WUJI_DEPS_KEY] || {}),
                '@wuji-gogo/runtime': gogoRuntime.runtimeShared,
            };
        };
        onMounted(async () => {
            if (isPreviewIFrame)
                return;
            try {
                const store = useStore();
                const aiProjectInfo = await store.dispatch('runtime/getAiProjectInfo', {
                    projectId: props.projectId,
                    env: props.env,
                    branchId: props.branch,
                });
                if (!hasCompleteArtifactCos(aiProjectInfo)) {
                    loadError.value = $t('k_dca31870');
                    loadStatus.value = 'error';
                    return;
                }
                // 与经典 runtime 对齐：加载 npm 依赖及其 bundle.css（如 md-editor-v3）
                await loadAIPageDependency(true, 'vue3');
                // 只 preload / 等待入口资源；页面 chunk 由 defineAsyncComponent 按需 import
                preloadArtifactCosAssets(aiProjectInfo.artifactCos);
                await loadArtifactCosStylesheets(aiProjectInfo.artifactCos);
                src.value = aiProjectInfo.artifactCos.js;
                loadStatus.value = 'ready';
            }
            catch (error) {
                console.error('[xy-client] load aiProjectInfo for remote runtime failed', error);
                loadError.value = $t('k_6be6212c', {
                    arg_0: error instanceof Error ? error.message : String(error),
                });
                loadStatus.value = 'error';
            }
        });
        onUnmounted(() => {
            disposeArtifactCosStylesheets();
        });
        return {
            wsUrl,
            hasPreviewBridge,
            src,
            isPreviewIFrame,
            loadStatus,
            loadError,
            routeViewContainer,
            handleHostReady,
            handlePointerDown,
            abortEventIfPicking,
            beforeLoadRemoteModule,
        };
    },
});
